
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@/types/user";
import { toast } from "sonner";

export interface FormData {
  name: string;
  role: string;
  institution: string;
  bio: string;
  website: string;
  twitter: string;
  linkedin: string;
  orcid: string;
}

export const useProfileData = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: "",
    role: "",
    institution: "",
    bio: "",
    website: "",
    twitter: "",
    linkedin: "",
    orcid: "",
  });

  useEffect(() => {
    // If not authenticated, redirect to auth page
    if (!user) {
      navigate("/auth", { replace: true });
      return;
    }

    // Fetch user profile from Supabase
    fetchUserProfile();
  }, [user, navigate]);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      if (!user?.id) return;

      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile');
        return;
      }

      if (data) {
        const userData = data as Record<string, any>;
        setUserProfile({
          id: userData.id as string,
          name: userData.name as string,
          role: userData.role as string,
          institution: userData.institution as string,
          bio: userData.bio as string || '',
          website: userData.website as string || '',
          twitter: userData.twitter as string || '',
          linkedin: userData.linkedin as string || '',
          orcid: userData.orcid as string || '',
          joinedDate: userData.joined_date as string || '',
          publications: userData.publications as number || 0,
          created_at: userData.created_at as string || '',
          updated_at: userData.updated_at as string || '',
        });
        setFormData({
          name: userData.name as string || "",
          role: userData.role as string || "",
          institution: userData.institution as string || "",
          bio: userData.bio as string || "",
          website: userData.website as string || "",
          twitter: userData.twitter as string || "",
          linkedin: userData.linkedin as string || "",
          orcid: userData.orcid as string || "",
        });
      }
    } catch (error) {
      console.error('Error in profile fetch:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    if (!user?.id || !userProfile) return;
    
    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('users')
        .update({
          name: formData.name,
          role: formData.role,
          institution: formData.institution,
          bio: formData.bio,
          website: formData.website,
          twitter: formData.twitter,
          linkedin: formData.linkedin,
          orcid: formData.orcid,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) {
        console.error('Error updating profile:', error);
        toast.error('Failed to update profile');
        return;
      }

      // Update local state
      setUserProfile({
        ...userProfile,
        ...formData,
        updated_at: new Date().toISOString(),
      });
      
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error in profile update:', error);
      toast.error('An unexpected error occurred');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      toast.error("Error signing out");
    }
  };

  return {
    userProfile,
    formData,
    setFormData,
    isEditing,
    setIsEditing,
    isLoading,
    isSaving,
    handleInputChange,
    handleSaveProfile,
    handleSignOut,
    fetchUserProfile,
  };
};
